// ***About.tsx

import React from 'react';
import { Link } from 'react-router-dom';
import PolicyAccordions from '../components/PolicyAccordions';

export default function About(): React.JSX.Element {
  return (
    <div className="w-full bg-slate-50 py-16 px-6 text-slate-900">
      <div className="mx-auto max-w-5xl space-y-16">

        {/* 1. HERO / MISSION BANNER */}
        <div className="text-center max-w-3xl mx-auto">
          <span className="inline-block rounded-full bg-mcc-lightest/40 px-3.5 py-1 text-xs font-semibold text-mcc-darkest border border-mcc-lightest uppercase tracking-wider mb-4">
            Our Purpose & Vision
          </span>
          <h1 className="text-3xl font-extrabold tracking-tight sm:text-4xl md:text-5xl text-mcc-darkest text-balance">
            Care for Real People, <br />
            <span className="text-mcc-neutral">By Real People.</span>
          </h1>

          {/* Mission Text with text-pretty to eliminate orphaned words */}
          <p className="mt-6 text-lg font-medium text-slate-600 leading-relaxed md:text-xl text-pretty">
            My Connective Care was founded on a simple truth: care is deeply personal. That is why a heart is embedded right at the core of our logo—it serves as our daily reminder that behind every schedule, care plan, and service visit are real human lives.
          </p>
        </div>

        {/* 2. THE TWO PILLARS: CARE RECIPIENTS & CAREGIVERS */}
        <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
          {/* Pillar 1: High Quality Care */}
          <div className="rounded-2xl border border-mcc-lightest/60 bg-white p-8 md:p-10 shadow-sm transition-all hover:border-mcc-light hover:shadow-md">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-mcc-lightest/30 text-mcc-darkest font-bold text-2xl mb-6 border border-mcc-lightest/50">
              ♥
            </div>
            <h2 className="text-2xl font-bold text-mcc-darkest">Excellence in Care</h2>
            <p className="mt-4 text-slate-600 leading-relaxed text-pretty">
              We aim to deliver top-quality care through caregivers who are not only rigorously certified, but genuinely compassionate. We believe that true caregiving goes far beyond completing tasks—it’s about establishing trust, maintaining dignity, and bringing peace of mind to families.
            </p>
          </div>

          {/* Pillar 2: Supporting Caregivers */}
          <div className="rounded-2xl border border-mcc-lightest/60 bg-white p-8 md:p-10 shadow-sm transition-all hover:border-mcc-light hover:shadow-md">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-mcc-lightest/30 text-mcc-darkest font-bold text-2xl mb-6 border border-mcc-lightest/50">
              🤝
            </div>
            <h2 className="text-2xl font-bold text-mcc-darkest">Caring for the Caregiver</h2>
            <p className="mt-4 text-slate-600 leading-relaxed text-pretty">
              We pride ourselves in taking care of those who care for others. Looking out for our caregivers is just as important to us as supporting our clients. When caregivers feel valued, supported, and respected, they are empowered to provide the absolute highest level of service.
            </p>
          </div>
        </div>

        {/* 3. VISION & INFRASTRUCTURE BANNER */}
        <div className="rounded-2xl bg-mcc-darkest p-8 md:p-12 text-white shadow-xl relative overflow-hidden">
          <div className="relative z-10 max-w-3xl">
            <h2 className="text-2xl font-bold sm:text-3xl text-white text-balance">
              Removing the Frustration from Care
            </h2>
            <p className="mt-4 text-mcc-lightest text-base md:text-lg leading-relaxed text-pretty">
              Finding and receiving quality care shouldn't feel overwhelming, disjointed, or bureaucratic. We are actively building towards a unified care coordination system that removes the friction, confusion, and stress from the entire process—connecting families, caregivers, and administrative staff effortlessly.
            </p>
            <div className="mt-8">
              <Link
                to="/signup"
                className="inline-flex items-center justify-center rounded-md bg-mcc-neutral px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-mcc-light shadow-md"
              >
                Join Our Care Network
              </Link>
            </div>
          </div>
        </div>

        {/* 4. PRIVACY & DATA USE POLICIES SECTION */}
        <section id="policies" className="rounded-2xl border border-mcc-lightest/60 bg-white p-8 md:p-12 shadow-sm text-center space-y-8">
          <div className="space-y-4 max-w-3xl mx-auto">
            <h2 className="text-2xl font-extrabold text-mcc-darkest sm:text-3xl">
              Our Commitment to Privacy & Data Protection
            </h2>
            <p className="text-base font-medium text-slate-600 leading-relaxed md:text-lg text-pretty">
              At My Connective Care, we take your data and privacy seriously. We are committed to protecting your personal and health information with strict confidentiality standards, transparent practices, and secure care coordination infrastructure. Please review our official documents below to see how your privacy is protected.
            </p>
          </div>

          <PolicyAccordions />
        </section>

      </div>
    </div>
  );
}
